import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2, FileDown } from "lucide-react";
import { API_BASE } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Sales() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [show, setShow] = useState(false);
  // Compteur incrémental pour générer un _uid stable par ligne (évite les bugs
  // de focus/saisie quand on supprime une ligne au milieu de la liste).
  const lineUidRef = useRef(1);
  const nextUid = () => lineUidRef.current++;
  const [form, setForm] = useState({ customer_name:"Client occasionnel", site_id:"", paid_amount:0, note:"", lines:[{_uid: 0, product_id:"",quantity:1,unit_price:0}] });

  const load = async () => {
    const [s, p, st] = await Promise.all([api.get("/sales"), api.get("/products"), api.get("/sites")]);
    setSales(s.data); setProducts(p.data); setSites(st.data);
  };
  useEffect(()=>{load();},[]);
  const productMap = useMemo(()=>Object.fromEntries(products.map(p=>[p.id, p])), [products]);
  const siteMap = useMemo(()=>Object.fromEntries(sites.map(s=>[s.id, s])), [sites]);

  const total = useMemo(() => form.lines.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.unit_price || 0), 0), [form.lines]);

  const updateLine = (i, field, val) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [field]: val };
    if (field === "product_id") {
      const p = products.find(pp => pp.id === val);
      if (p) lines[i].unit_price = p.price;
    }
    setForm({ ...form, lines });
  };
  const addLine = () => setForm({ ...form, lines: [...form.lines, { _uid: nextUid(), product_id:"", quantity:1, unit_price:0 }] });
  const rmLine = i => setForm({ ...form, lines: form.lines.filter((_,j)=>j!==i) });

  const submit = async e => {
    e.preventDefault();
    try {
      const payload = {
        customer_id: null,
        customer_name: form.customer_name || "Client occasionnel",
        site_id: form.site_id,
        paid_amount: Number(form.paid_amount || 0),
        note: form.note,
        lines: form.lines.map(l => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_price: Number(l.unit_price) })),
      };
      await api.post("/sales", payload);
      toast.success("Vente enregistrée");
      setShow(false); setForm({ customer_name:"Client occasionnel", site_id:"", paid_amount:0, note:"", lines:[{_uid: nextUid(), product_id:"",quantity:1,unit_price:0}] });
      load();
    } catch(e){ toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Ventes" subtitle="Caisse · Encaissements"
        actions={canEdit && <button data-testid="new-sale-btn" className="btn-primary flex items-center gap-2" onClick={()=>setShow(!show)}><Plus size={14}/>Nouvelle vente</button>}
      />

      {show && canEdit && (
        <Surface title="Nouvelle vente">
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><label className="label-mono block mb-1.5">Nom du client</label>
                <input data-testid="sale-customer-name" className="input-base" placeholder="Client occasionnel" value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})}/>
              </div>
              <div><label className="label-mono block mb-1.5">Site</label>
                <select data-testid="sale-site" required className="input-base" value={form.site_id} onChange={e=>setForm({...form,site_id:e.target.value})}>
                  <option value="">— Choisir —</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div><label className="label-mono block mb-1.5">Montant encaissé (F)</label><input data-testid="sale-paid" type="number" step="0.01" className="input-base" value={form.paid_amount} onChange={e=>setForm({...form,paid_amount:e.target.value})}/></div>
            </div>
            <div className="space-y-2">
              <div className="label-mono">Articles</div>
              {form.lines.map((l, i) => (
                <div key={l._uid ?? `legacy-${i}`} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-6"><select required className="input-base" value={l.product_id} onChange={e=>updateLine(i,"product_id",e.target.value)}>
                    <option value="">— Produit —</option>{products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select></div>
                  <div className="col-span-2"><input type="number" min="1" required className="input-base" placeholder="Qté" value={l.quantity} onChange={e=>updateLine(i,"quantity",e.target.value)}/></div>
                  <div className="col-span-3"><input type="number" step="0.01" required className="input-base" placeholder="Prix unit." value={l.unit_price} onChange={e=>updateLine(i,"unit_price",e.target.value)}/></div>
                  <div className="col-span-1"><button type="button" onClick={()=>rmLine(i)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)]"><Trash2 size={14}/></button></div>
                </div>
              ))}
              <button type="button" onClick={addLine} className="btn-ghost text-xs">+ Ajouter</button>
            </div>
            <div className="flex justify-between items-center pt-3 border-t border-[var(--border-line)]">
              <div className="font-heading text-2xl"><span className="label-mono mr-3">Total</span><span className="font-data neon-text-cyan">{total.toLocaleString("fr-FR")} F</span></div>
              <div className="flex gap-2"><button data-testid="submit-sale" type="submit" className="btn-primary">Valider la vente</button><button type="button" className="btn-ghost" onClick={()=>setShow(false)}>Annuler</button></div>
            </div>
          </form>
        </Surface>
      )}

      <Surface title="Historique">
        {sales.length === 0 ? <Empty/> : (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Client</th><th>Site</th><th>Articles</th><th>Total</th><th>Encaissé</th><th>Reliquat</th><th>Statut</th><th>Reçu</th></tr></thead>
            <tbody>
              {sales.map(s => (
                <tr key={s.id}>
                  <td className="font-data text-[var(--text-muted)] text-xs">{s.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td>{s.customer_name}</td>
                  <td className="text-[var(--text-muted)]">{siteMap[s.site_id]?.name}</td>
                  <td className="font-data">{s.lines.length}</td>
                  <td className="font-data">{s.total.toLocaleString("fr-FR")} F</td>
                  <td className="font-data neon-text-green">{s.paid.toLocaleString("fr-FR")} F</td>
                  <td className="font-data neon-text-pink">{s.balance.toLocaleString("fr-FR")} F</td>
                  <td>{s.status === "payée" ? <span className="badge-success">PAYÉE</span> : s.status === "partielle" ? <span className="badge-warn">PARTIELLE</span> : <span className="badge-alert">IMPAYÉE</span>}</td>
                  <td>
                    <a data-testid={`pdf-${s.id}`} href={`${API_BASE}/sales/${s.id}/pdf?token=${localStorage.getItem("ps_token") || ""}`} target="_blank" rel="noopener noreferrer"
                       onClick={async (e) => {
                         e.preventDefault();
                         const r = await api.get(`/sales/${s.id}/pdf`, { responseType: "blob" });
                         const url = URL.createObjectURL(r.data);
                         const a = document.createElement("a"); a.href = url; a.download = `recu-${s.id.slice(0,8)}.pdf`; a.click();
                       }}
                       className="text-[var(--neon-cyan)] hover:underline flex items-center gap-1 font-data text-xs"><FileDown size={12}/>PDF</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
